import { useMemo } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { format } from "date-fns";

import { xeroPushNoun } from "./types";

import type { IXeroPushOutcome, IXeroPushRow } from "./types";

interface XeroPushResultProps {
  /** The merged outcome of the last push. Absent means the drawer is closed. */
  outcome?: IXeroPushOutcome;
  /** Record id -> display name, from the calling grid's rows. */
  labels: Record<number, string>;
  /** Who pushed: the connected user's Xero email, when the state has answered. */
  pushedBy?: string;
  /**
   * When the push happened, already formatted. A live push has no timestamp of
   * its own, so it falls back to now; a historical batch passes its own.
   */
  pushedAt?: string;
  /** A push is in flight: Retry failed would be swallowed by the hook's mutex. */
  loading?: boolean;
  onClose: () => void;
  onRetryFailed: (ids: number[]) => void;
}

/** Skipped records were already in Xero, so they are not failures to retry. */
const isFailed = (row: IXeroPushRow) => !row.ok && !row.skipped;

/** Per-record outcome of the last push. Open whenever there is an outcome. */
const XeroPushResult: React.FC<XeroPushResultProps> = ({
  outcome,
  labels,
  pushedBy,
  pushedAt: pushedAtProp,
  loading = false,
  onClose,
  onRetryFailed,
}) => {
  // The outcome carries no timestamp, so the drawer stamps the time it first
  // rendered this result. The drawer opens on the push answer, so that is the
  // push time to the minute the subline shows.
  const stampedNow = useMemo(
    () => (outcome ? format(new Date(), "d MMM yyyy, HH:mm") : undefined),
    [outcome]
  );
  const pushedAt = pushedAtProp ?? stampedNow;

  const rows = outcome?.results ?? [];
  const batch = outcome?.batches[0];
  const failedIds = rows.filter(isFailed).map((row) => row.id);
  // A failed chunk stops the push, so the ids behind it have no row here at
  // all. Without this the header reads "Pushed 50 invoices · 50 succeeded, 0
  // failed" for a 120-id selection and says nothing about the other 70.
  const notAttempted = Math.max(0, (outcome?.requested ?? rows.length) - rows.length);

  // Only the batch code is optional in the design, but POST /{kind}/push
  // answers `batch_id: null` today, so a subline hung off the code would never
  // render. Join whichever segments exist instead, and drop the whole line only
  // when there is nothing to say but the time.
  const subline = [
    batch ? `Batch ${batch} · ${pushedAt}` : pushedAt,
    pushedBy ? `pushed by ${pushedBy}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Drawer
      anchor="right"
      open={outcome != null}
      onClose={onClose}
      PaperProps={{ sx: { width: 480, maxWidth: "100vw" } }}
    >
      <Box role="region" aria-label="Xero push result">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "grey.200",
            pl: 3,
            pr: 2.5,
            py: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {outcome ? (
              <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
                {`Pushed ${rows.length} ${xeroPushNoun(
                  outcome.kind,
                  rows.length
                )} · ${outcome.ok} succeeded, ${outcome.failed} failed${
                  notAttempted > 0 ? ` · ${notAttempted} not attempted` : ""
                }`}
              </Typography>
            ) : null}
            {batch || pushedBy ? (
              <Typography variant="caption" color="text.secondary">
                {subline}
              </Typography>
            ) : null}
          </Box>
          <IconButton
            aria-label="Close results"
            size="small"
            onClick={onClose}
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <List disablePadding sx={{ py: 0.5 }}>
          {rows.map((row, i) => (
            <ListItem
              key={`${row.id}-${i}`}
              divider={i < rows.length - 1}
              alignItems="flex-start"
              sx={{ px: 3, py: 1.375 }}
            >
              <ListItemIcon sx={{ minWidth: 30, mt: 0.375 }}>
                {isFailed(row) ? (
                  <ErrorIcon sx={{ fontSize: 18, color: "error.main" }} />
                ) : (
                  <CheckCircleIcon
                    sx={{ fontSize: 18, color: "success.main" }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                disableTypography
                primary={
                  <Typography sx={{ fontSize: 14 }}>
                    {labels[row.id] ?? `#${row.id}`}
                  </Typography>
                }
                secondary={
                  <>
                    {isFailed(row) ? (
                      <Typography sx={{ fontSize: 12, color: "error.main" }}>
                        {row.error ?? "Push failed"}
                      </Typography>
                    ) : null}
                    {!isFailed(row) && row.xero_id ? (
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "primary.main",
                        }}
                      >
                        {row.xero_id}
                      </Typography>
                    ) : null}
                    {row.warning ? (
                      <Typography sx={{ fontSize: 12, color: "warning.main" }}>
                        {row.warning}
                      </Typography>
                    ) : null}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderTop: 1,
            borderColor: "divider",
            px: 3,
            py: 1.75,
          }}
        >
          {failedIds.length > 0 ? (
            <Button
              variant="contained"
              color="primary"
              disabled={loading}
              onClick={() => onRetryFailed(failedIds)}
            >
              {`Retry failed (${failedIds.length})`}
            </Button>
          ) : null}
          <Button color="primary" onClick={onClose}>
            Close
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default XeroPushResult;
